import React from 'react';
import { dispatch } from '@wfp-root-app/store/store';

import { MobileDevicesFilters } from './MobileDevicesFilters';
import { useMobileDevicesList } from './utils/useMobileDevicesList';
import { SortableTableHead } from '../utils/table/table-head/sortable-table-head';
import PagingComponent from '../utils/paging';
import { useAuth } from './hooks/useAuth';
import { ManagerPermission } from '../permission-profiles/permission';
import { unblockMobileDevice, blockMobileDevice } from '../apiClient';
import { successAction, errorAction } from '../utils/notifications';
import { ButtonWithModalConfirmation } from '../utils/components/button-with-modal-confirmation';

interface Props {}

const MOBILES_PER_PAGE = 20;

export default function MobileDevicesList({}: Props) {
    const {
        handleSort,
        mobileDevices,
        error,
        queryOptions,
        activeFilters,
        setActiveFilters,
        activePage,
        setActivePage,
        total,
        setTotal,
        refresh,
    } = useMobileDevicesList();
    const { hasFeatureAccess } = useAuth();

    const canUnlock = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePark);
    const canBlock = hasFeatureAccess(ManagerPermission.mobileDevicesCreateUpdatePark);

    const onUnblockClick = async (e: React.MouseEvent, id: number) => {
        e.preventDefault();
        try {
            await unblockMobileDevice(id);
            dispatch(successAction(`Mobile device unlock request created for id=${id}.`));
        } catch (err) {
            let message: string = null;
            try {
                const response: unknown = err;
                if (response && typeof (response as { text?: unknown }).text === 'function') {
                    const text = await (response as { text: () => Promise<string> }).text();
                    try {
                        const json = text ? JSON.parse(text) : null;
                        if (json && typeof json === 'object') {
                            message =
                                (json as { message?: string; error?: string }).message ||
                                (json as { message?: string; error?: string }).error ||
                                null;
                        } else if (typeof json === 'string') {
                            message = json;
                        }
                    } catch {
                        message = text;
                    }
                }
            } catch {}
            dispatch(errorAction(message || String(err)));
        } finally {
            await refresh();
        }
    };

    const onBlockClick = async (e: React.MouseEvent, id: number) => {
        e.preventDefault();
        try {
            await blockMobileDevice(id);
            dispatch(successAction(`Mobile device block request created for id=${id}.`));
        } catch (err) {
            let message: string = null;
            try {
                const response: unknown = err;
                if (response && typeof (response as { text?: unknown }).text === 'function') {
                    const text = await (response as { text: () => Promise<string> }).text();
                    try {
                        const json = text ? JSON.parse(text) : null;
                        if (json && typeof json === 'object') {
                            message =
                                (json as { message?: string; error?: string }).message ||
                                (json as { message?: string; error?: string }).error ||
                                null;
                        } else if (typeof json === 'string') {
                            message = json;
                        }
                    } catch {
                        message = text;
                    }
                }
            } catch {}
            dispatch(errorAction(message || String(err)));
        } finally {
            await refresh();
        }
    };

    // Handle error state
    if (error) {
        return (
            <div className="wfp-alert wfp-alert--error">
                <p>
                    <strong>Error:</strong> {error}
                </p>
                <button className="wfp-btn wfp-btn--primary" onClick={refresh} type="button">
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div>
            <MobileDevicesFilters
                activeFilters={activeFilters}
                activePage={activePage}
                refresh={refresh}
                setActiveFilters={setActiveFilters}
                setTotal={setTotal}
                sortOptions={queryOptions}
            />

            <table className="wfp-table mt4 table-hover">
                <SortableTableHead changeSortOptions={handleSort}>
                    <th>ID</th>
                    <th>Hardware ID</th>
                    <th>APK Type</th>
                    <th>Vendor/Partner</th>
                    <th>Outlet</th>
                    <th>Login Attempts</th>
                    <th>Blocked</th>
                    <th>Created At</th>
                    <th>Last TX At</th>
                </SortableTableHead>
                <tbody>
                    {mobileDevices.length === 0 ? (
                        <tr>
                            <td className="text-center" colSpan={8}>
                                No mobile devices found.
                            </td>
                        </tr>
                    ) : (
                        mobileDevices.map((device) => (
                            <tr key={device.id}>
                                <td>{device.id}</td>
                                <td>{device.imei}</td>
                                <td>{device.apkType}</td>
                                <td>
                                    {device.apkType === 'partner' ? (
                                        <>
                                            {device.partnerName}
                                            {device.partnerId != null && (
                                                <>
                                                    {' '}
                                                    <strong>[{device.partnerId}]</strong>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {device.vendorName}
                                            {device.vendorId && (
                                                <>
                                                    {' '}
                                                    <strong>[{device.vendorId}]</strong>
                                                </>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td>{device.outlet || '-'}</td>
                                <td>{device.passwordAttempts}</td>
                                <td>
                                    {device.status === 'blocked' ? (
                                        <span>
                                            {canUnlock ? (
                                                <>
                                                    <ButtonWithModalConfirmation
                                                        buttonClassName="wfp-btn"
                                                        customModalBody={
                                                            <span>
                                                                Are you sure you want to park an unblock request for
                                                                mobile device ID {device.id} (Hardware ID {device.imei}
                                                                )?
                                                            </span>
                                                        }
                                                        customModalTitle="Confirm Unblock"
                                                        onApprove={(e) => onUnblockClick(e, device.id)}
                                                    >
                                                        Unblock
                                                    </ButtonWithModalConfirmation>
                                                </>
                                            ) : (
                                                'Yes'
                                            )}
                                        </span>
                                    ) : (
                                        <span>
                                            {canBlock ? (
                                                <ButtonWithModalConfirmation
                                                    buttonClassName="wfp-btn wfp-btn--danger"
                                                    customModalBody={
                                                        <span>
                                                            Are you sure you want to park a block request for mobile
                                                            device ID {device.id} (Hardware ID {device.imei})?
                                                        </span>
                                                    }
                                                    customModalTitle="Confirm Block"
                                                    onApprove={(e) => onBlockClick(e, device.id)}
                                                >
                                                    Block
                                                </ButtonWithModalConfirmation>
                                            ) : (
                                                'No'
                                            )}
                                        </span>
                                    )}
                                </td>
                                <td>{new Date(device.createdAt).toLocaleDateString()}</td>
                                <td>
                                    {device.lastTransactionAt
                                        ? new Date(device.lastTransactionAt).toLocaleString()
                                        : ''}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {mobileDevices.length > 0 && (
                <PagingComponent
                    onPageChanged={(page: number) => {
                        setActivePage(page);
                    }}
                    paging={{
                        offset: (activePage - 1) * MOBILES_PER_PAGE,
                        limit: MOBILES_PER_PAGE,
                        totalAtLeast: total,
                    }}
                />
            )}
        </div>
    );
}
